import {
    Country,
    EstimatedReviewTime,
    FaqUrl,
    Locale,
    MarketConsentOptions,
    MaxReviewTime,
    MetadataConfig,
    Options,
    ProgramTheme,
    ProgramThemeMessages,
    Segment,
    VerificationServiceValues,
} from '../types/types';
import { SegmentEnum } from '../../lib/types/runtimeTypes';
import { assertValidSegmentName } from '../../lib/types/assertions';
import { logger } from '../utils/logger/logger';
import { getOptions } from '../../options/options';
import { arrayUnique } from '../utils/arrays';
import { deepClone, getSafe } from '../utils/objects';

export const getCompanyName = (programTheme: ProgramTheme): string => {
    let companyName = '{Company}'; // Show literal string "{Company}" so implementor is prompted to fix by adding in my.sheerid.com
    try {
        companyName = programTheme.intl.messages.companyName;
        if (!(typeof companyName === 'string' && companyName.length > 0)) {
            throw new Error('Invalid companyName');
        }
    } catch (e) {
        // Property access or invalid company name error
        logger.warn('companyName not found. Please sign in to my.sheerid.com to set.');
    }

    return companyName;
};

const defaultMarketConsent: MarketConsentOptions = {
    enabled: false,
    required: false,
    message: '',
};

const defaultMetadata: MetadataConfig = Object.freeze({
    enabled: false,
    keys: [],
});

export const getMetadataConfig = (programTheme: ProgramTheme): MetadataConfig => {
    let customMetadata: MetadataConfig = deepClone(defaultMetadata);

    try {
        if (getSafe<boolean>(() => programTheme.config.customMetadata.enabled)) {
            customMetadata = programTheme.config.customMetadata;
        }
    } catch (e) {
        logger.warn('No custom metadata provided by the server.');
    }
    if (getOptions().customMetadata) {
        customMetadata = getOptions().customMetadata;
    }

    return customMetadata;
};

export const getMarketConsent = (programTheme: ProgramTheme): MarketConsentOptions => {
    let marketConsent: MarketConsentOptions = Object.assign({}, defaultMarketConsent);
    try {
        if (programTheme.config.marketConsent) {
            marketConsent = programTheme.config.marketConsent;
        }
    } catch (e) {
        // market consent is not coming for this program from server. Default will be used
        logger.warn('Server did not provided a market consent config. Default will be used');
    }
    if (getOptions().marketConsent) {
        marketConsent = getOptions().marketConsent;
    }
    return marketConsent;
};

export const getThemeMessages = (programTheme: ProgramTheme): ProgramThemeMessages => {
    let themeMessages: ProgramThemeMessages;
    try {
        themeMessages = programTheme.intl.messages;
    } catch (e) {
        logger.warn('Unable to get theme messages from ', programTheme);
    }
    return themeMessages;
};

/**
 * @description Return locales configured in MySheerID for this program, or specified in Javascript options.
 */
export const getAvailableLocales = (programTheme: ProgramTheme): Locale[] => {
    let configuredLocales: Locale[] = ['en-US'];
    let themeLocales: Locale[] = [];
    try {
        themeLocales = programTheme.config.locales;
    } catch (e) {
        logger.warn('Unable to get configured locales from programTheme');
    }
    const optionsLocales: Locale[] = getLocalesFromOptions(getOptions());
    configuredLocales = [...themeLocales, ...optionsLocales];

    return arrayUnique(configuredLocales);
};

/**
 * @private
 */
export const getLocalesFromOptions = (options: Options): Locale[] => {
    if (options && options.messagesWithLocale) {
        return Object.keys(options.messagesWithLocale) as Locale[];
    }
    return [];
};

/**
 * @description Safely return the countries that were chosen/configured in MySheerID for this program.
 * Otherwise, return US as default.
 */
export const getConfiguredCountries = (programTheme: ProgramTheme): Country[] => {
    try {
        if (programTheme && programTheme.config && programTheme.config.countries) {
            return programTheme.config.countries;
        }

        // Old location for this property, may be returned from the server
        // @ts-ignore
        if (programTheme.intl.messages.step.personalInfo.countries) {
            const availableCountries: Country[] = [];

            // @ts-ignore
            for (const countryCode in programTheme.intl.messages.step.personalInfo.countries) {
                availableCountries.push(countryCode as Country);
            }
            return availableCountries;
        }
    } catch (e) {
        logger.warn('Unable to get configured countries');
    }
    return ['US'];
};

export const getEstimatedReviewTime = (programTheme: ProgramTheme): EstimatedReviewTime => {
    let ewt: EstimatedReviewTime;
    try {
        // Added in API ver 2.5.x, may not be present for all
        ewt = programTheme.config.estimatedReviewTime;
    } catch (e) {
        ewt = 'A_FEW_HOURS';
    }
    return ewt;
};

export const getMaxReviewTime = (programTheme: ProgramTheme): MaxReviewTime => {
    let mrt: MaxReviewTime;
    try {
        // Added in API ver 2.5.x, may not be present for all
        mrt = programTheme.config.maxReviewTime;
    } catch (e) {
        mrt = '2_HOURS';
    }
    return mrt;
};

const options = getOptions();
const getFaqUrl = (segment: Segment):string => {
    assertValidSegmentName(segment);
    switch (segment) {
        case SegmentEnum.STUDENT:
            return options.urlStudentFaq;
        case SegmentEnum.SENIOR:
            return options.urlSeniorFaq;
        case SegmentEnum.TEACHER:
            return options.urlTeacherFaq;
        case SegmentEnum.MILITARY:
            return options.urlMilitaryFaq;
        case SegmentEnum.FIRST_RESPONDER:
            return options.urlFirstResponderFaq;
        case SegmentEnum.MEDICAL:
            return options.urlMedicalFaq;
        case SegmentEnum.EMPLOYMENT:
            return options.urlEmploymentFaq;
    }
};

export const getFaqLink = (
    programTheme: ProgramTheme,
    segment: Segment,
    programId: VerificationServiceValues["programId"],
): FaqUrl => {
    let faqInTheme: FaqUrl;
    try {
        faqInTheme = programTheme.config.customFaqLink;
    } catch (e) {
        faqInTheme = '';
    }

    return (!faqInTheme || faqInTheme === '') ?
        `${getFaqUrl(segment)}?pid=${programId}` :
        faqInTheme;
}
